import type { ColoredMaterial } from "@color-finder/color-finder-api/models";
import {
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { CountrySelect } from "pages/profile/CountrySelect";
import { DIALOG_LIST } from "./ContactRequestDialog";

interface RequestSampleProps {
  requestData: any;
  onChange: (value: any) => void;
  isNext?: boolean;
  dialogType: string;
  isSendAction?: boolean;
  products?: ColoredMaterial[];
  isLoadingProducts?: boolean;
  onSearchQueryChange?: (query: string) => void;
  searchQuery?: string;
}

const industryList = [
  { label: "Automotive", value: "Automotive" },
  { label: "Aviation", value: "Aviation" },
  { label: "Bikes & motorcycles", value: "Bikes & motorcycles" },
  {
    label: "Construction & architectural",
    value: "Construction & architectural",
  },
  { label: "Cosmetics", value: "Cosmetics" },
  {
    label: "Electronics, appliances & telecommunications",
    value: "Electronics, appliances & telecommunications",
  },
  { label: "Energy", value: "Energy" },
  { label: "Healthcare", value: "Healthcare" },
  {
    label: "Industrial and agricultural applications",
    value: "Industrial & agricultural applications",
  },
  { label: "Marine & ships", value: "Marine & ships" },
  { label: "Packaging & print", value: "Packaging & print" },
  { label: "Rail", value: "Rail" },
  { label: "Reefer containers", value: "Reefer containers" },
  { label: "Security & protection", value: "Security & protection" },
  { label: "Sports & leisure", value: "Sports & leisure" },
  { label: "Wood & furniture", value: "Wood & furniture" },
];

export const RequestSampleDialogContent = ({
  requestData,
  onChange,
  isNext = false,
  dialogType,
  isSendAction = false,
  products = [],
  isLoadingProducts = false,
  onSearchQueryChange,
  searchQuery = "",
}: RequestSampleProps) => {
  const countryChange = (code?: string) => {
    if (code) onChange({ ...requestData, cov_country: code });
  };

  const handleProductChange = (_: any, value: ColoredMaterial | null) => {
    if (value) {
      onChange({
        ...requestData,
        selectedProduct: value,
        cov_product_name: value.material.publicGradeName,
        colorCode:
          value.color.finalColorCode || value.color.preliminaryColorCode,
      });
    } else {
      onChange({
        ...requestData,
        selectedProduct: null,
        cov_product_name: "",
        colorCode: "",
      });
    }
  };

  return (
    <DialogContent
      className="dialog-confirm"
      dividers
      sx={{ overflow: "hidden", border: "none", padding: 0, overflowY: "auto" }}
    >
      <Box sx={{ width: "100%", marginTop: 0 }}>
        {isNext ? (
          <Box
            component="form"
            sx={{
              display: "flex",
              flexWrap: "wrap",
              "& .MuiTextField-root": {
                width: "100%",
                height: "56px",
                borderRadius: "4px",
                marginTop: "24px",
              },
              "& .MuiAutocomplete-root": { width: "100%", marginTop: "24px" },
              "& div.MuiInputBase-root": {
                borderRadius: "4px",
              },
            }}
          >
            <Autocomplete
              id="ProductName"
              options={products}
              value={requestData?.selectedProduct || null}
              onChange={handleProductChange}
              onInputChange={(_, newValue) => {
                // Trigger server-side search when user types
                if (onSearchQueryChange) {
                  onSearchQueryChange(newValue);
                }
              }}
              getOptionLabel={(option) => option.material.publicGradeName || ""}
              loading={isLoadingProducts}
              filterOptions={(options, { inputValue }) => {
                // First filter to only ACTIVE products
                const activeOptions = options.filter(
                  (option) => option.availability?.colorStatus === "ACTIVE",
                );

                // If we have a search query (>= 2 chars), don't filter client-side
                // The server has already done the filtering
                if (searchQuery.length >= 2) {
                  return activeOptions;
                }
                // For initial products (no search query), do client-side filtering
                if (!inputValue) return activeOptions;
                const searchTerm = inputValue.toLowerCase();
                return activeOptions.filter((option) => {
                  const gradeName =
                    option.material.publicGradeName?.toLowerCase() || "";
                  const colorCode =
                    (
                      option.color.finalColorCode ||
                      option.color.preliminaryColorCode
                    )?.toLowerCase() || "";
                  return (
                    gradeName.includes(searchTerm) ||
                    colorCode.includes(searchTerm)
                  );
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  error={
                    isSendAction &&
                    (!requestData?.selectedProduct ||
                      !requestData?.cov_product_name)
                  }
                  label="Product Name"
                  placeholder="Type to search products..."
                  helperText={
                    isLoadingProducts
                      ? "Searching..."
                      : searchQuery.length >= 2
                        ? `Found ${products.length} products matching "${searchQuery}"`
                        : products.length > 0
                          ? `Showing top ${products.length} products. Type to search all products.`
                          : undefined
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingProducts ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  key={`${option.eljExperimentId}-${option.eljExperimentBatch}`}
                >
                  <Box>
                    <Box sx={{ fontWeight: 500 }}>
                      {option.material.publicGradeName}
                    </Box>
                    <Box sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                      {option.color.finalColorCode ||
                        option.color.preliminaryColorCode}
                    </Box>
                  </Box>
                </Box>
              )}
            />
            <TextField
              error={
                isSendAction &&
                (!requestData?.colorCode ||
                  (requestData?.colorCode &&
                    requestData?.colorCode.length === 0))
              }
              required
              id="ColorCode"
              label={"Color Code"}
              value={requestData?.colorCode || ""}
              InputProps={{
                readOnly: true,
              }}
              helperText="Automatically filled from selected product"
            />
            <TextField
              error={
                isSendAction &&
                (!requestData?.cov_quantity_per_kg ||
                  (requestData?.cov_quantity_per_kg &&
                    requestData?.cov_quantity_per_kg.length === 0))
              }
              required
              id="Quantity"
              label={"Quantity (kg)"}
              value={requestData?.cov_quantity_per_kg}
              onChange={(e) =>
                onChange({
                  ...requestData,
                  cov_quantity_per_kg: e.target.value,
                })
              }
            />
            {dialogType === DIALOG_LIST.REQUEST_QUOTE && (
              <TextField
                error={
                  isSendAction &&
                  (!requestData?.cov_potentl_yly_qty ||
                    (requestData?.cov_potentl_yly_qty &&
                      requestData?.cov_potentl_yly_qty.length === 0))
                }
                required
                id="PotentialYearlyQuantity"
                label={"Potential Yearly Quantity (kg)"}
                value={requestData?.cov_potentl_yly_qty}
                onChange={(e) =>
                  onChange({
                    ...requestData,
                    cov_potentl_yly_qty: e.target.value,
                  })
                }
              />
            )}
            {dialogType === DIALOG_LIST.REQUEST_QUOTE && (
              <TextField
                error={
                  isSendAction &&
                  (!requestData?.cov_price_per_kg ||
                    (requestData?.cov_price_per_kg &&
                      requestData?.cov_price_per_kg.length === 0))
                }
                required
                id="priceKg"
                label={"Price/kg"}
                value={requestData?.cov_price_per_kg}
                onChange={(e) =>
                  onChange({ ...requestData, cov_price_per_kg: e.target.value })
                }
              />
            )}
            {dialogType === DIALOG_LIST.REQUEST_QUOTE && (
              <FormControl
                sx={{ width: "100%", height: "56px", marginTop: "24px" }}
                required
                error={isSendAction && !requestData?.cov_currency}
              >
                <InputLabel id="select-currency">Currency</InputLabel>
                <Select
                  sx={{ borderRadius: "4px" }}
                  labelId="select-currency"
                  id="selectCurrency"
                  value={requestData?.cov_currency || ""}
                  label={"Currency*"}
                  onChange={(e) =>
                    onChange({ ...requestData, cov_currency: e.target.value })
                  }
                >
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                </Select>
              </FormControl>
            )}
            <TextField
              error={
                isSendAction &&
                (!requestData?.cov_shipping_addr ||
                  (requestData?.cov_shipping_addr &&
                    requestData?.cov_shipping_addr.length === 0))
              }
              required
              id="ShippingAddress"
              label={"Shipping Address"}
              value={requestData?.cov_shipping_addr}
              multiline
              maxRows={4}
              onChange={(e) =>
                onChange({ ...requestData, cov_shipping_addr: e.target.value })
              }
            />
            <DatePicker
              label="Earliest Delivery Date*"
              value={requestData?.cov_earliest_delivery || null}
              onChange={(newValue: any) =>
                onChange({ ...requestData, cov_earliest_delivery: newValue })
              }
              slotProps={{
                textField: {
                  required: true,
                  error: isSendAction && !requestData?.cov_earliest_delivery,
                  sx: { marginTop: "24px" },
                },
              }}
            />
            <DatePicker
              label="Preferred Delivery Date*"
              value={requestData?.cov_preferred_delivery || null}
              onChange={(newValue: any) =>
                onChange({ ...requestData, cov_preferred_delivery: newValue })
              }
              slotProps={{
                textField: {
                  required: true,
                  error: isSendAction && !requestData?.cov_preferred_delivery,
                  sx: { marginTop: "24px" },
                },
              }}
            />
            <TextField
              error={
                isSendAction &&
                (!requestData?.cov_lead_comments ||
                  (requestData?.cov_lead_comments &&
                    requestData?.cov_lead_comments.length === 0))
              }
              required
              id="RequestComment"
              label={"Comment"}
              value={requestData?.cov_lead_comments}
              multiline
              maxRows={10}
              onChange={(e) =>
                onChange({ ...requestData, cov_lead_comments: e.target.value })
              }
            />
          </Box>
        ) : (
          <Box
            component="form"
            sx={{
              display: "flex",
              flexWrap: "wrap",
              "& .MuiTextField-root": {
                width: "100%",
                height: "56px",
                borderRadius: "4px",
                marginTop: "16px",
              },
              "& .MuiAutocomplete-root": { width: "100%" },
              "& div.MuiInputBase-root": {
                borderRadius: "4px",
              },
            }}
          >
            <TextField
              error={
                isSendAction &&
                (!requestData?.firstName ||
                  (requestData?.firstName &&
                    requestData?.firstName.length === 0))
              }
              required
              id="outlined-required"
              label={"First Name"}
              value={requestData?.firstName}
              onChange={(e) =>
                onChange({ ...requestData, firstName: e.target.value })
              }
            />
            <TextField
              error={
                isSendAction &&
                (!requestData?.lastName ||
                  (requestData?.lastName && requestData?.lastName.length === 0))
              }
              required
              id="outlined-required"
              label={"Last Name"}
              value={requestData?.lastName}
              onChange={(e) =>
                onChange({ ...requestData, lastName: e.target.value })
              }
            />
            <TextField
              error={
                isSendAction &&
                (!requestData?.primaryEmail ||
                  (requestData?.primaryEmail &&
                    requestData?.primaryEmail.length === 0))
              }
              required
              id="outlined-required"
              label={"Email Address"}
              value={requestData?.primaryEmail}
              onChange={(e) =>
                onChange({ ...requestData, primaryEmail: e.target.value })
              }
            />
            <TextField
              error={
                isSendAction &&
                (!requestData?.cov_company ||
                  (requestData?.cov_company &&
                    requestData?.cov_company.length === 0))
              }
              required
              id="outlined-required"
              label={"Company"}
              value={requestData?.cov_company}
              onChange={(e) =>
                onChange({ ...requestData, cov_company: e.target.value })
              }
            />
            <CountrySelect
              isSendAction={isSendAction}
              countryChange={countryChange}
              country={requestData?.cov_country}
              labelText="Select Country"
            />
            <FormControl
              sx={{ width: "100%", height: "56px", marginTop: "10px" }}
              required
              error={isSendAction && !requestData?.cov_industry}
            >
              <InputLabel id="select-category">Select Industry</InputLabel>
              <Select
                sx={{ borderRadius: "4px" }}
                labelId="select-category"
                id="selectCategory"
                value={requestData?.cov_industry || ""}
                label={"Industry*"}
                onChange={(e) =>
                  onChange({ ...requestData, cov_industry: e.target.value })
                }
              >
                {industryList.map((v, i) => (
                  <MenuItem key={v.value + i} value={v.value}>
                    {v.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              error={
                isSendAction &&
                (!requestData?.primaryPhone ||
                  (requestData?.primaryPhone &&
                    requestData?.primaryPhone.length === 0))
              }
              required
              id="outlined-required"
              label={"Phone Number"}
              value={requestData?.primaryPhone}
              onChange={(e) =>
                onChange({ ...requestData, primaryPhone: e.target.value })
              }
            />
            <TextField
              error={
                isSendAction &&
                (!requestData?.cov_job_title ||
                  (requestData?.cov_job_title &&
                    requestData?.cov_job_title.length === 0))
              }
              required
              id="outlined-required"
              label={"Job Title"}
              value={requestData?.cov_job_title}
              multiline
              maxRows={2}
              onChange={(e) =>
                onChange({ ...requestData, cov_job_title: e.target.value })
              }
            />
          </Box>
        )}
      </Box>
      {!isNext && (
        <Box sx={{ width: "100%", marginTop: "16px" }}>
          <FormGroup sx={{ marginTop: "16px", marginBottom: "36px" }}>
            <FormControlLabel
              sx={{
                ">.MuiFormControlLabel-label": {
                  fontFamily: 'var(--font-family, "AktivGrotesk")',
                  fontWeight: 400,
                  fontSize: "var(--body-b2-14-regular-size, 14px)",
                },
              }}
              control={
                <Checkbox
                  required
                  checked={requestData?.cov_extrnl_consent}
                  onChange={() =>
                    onChange({
                      ...requestData,
                      cov_extrnl_consent: !requestData?.cov_extrnl_consent,
                    })
                  }
                />
              }
              label={
                <>
                  I have taken notice of the{" "}
                  <a
                    href="https://solutions.covestro.com/en/footersites/privacy-statement"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    privacy statement
                  </a>
                  .
                </>
              }
            />
            <FormControlLabel
              sx={{
                ">.MuiFormControlLabel-label": {
                  fontFamily: 'var(--font-family, "AktivGrotesk")',
                  fontWeight: 400,
                  fontSize: "var(--body-b2-14-regular-size, 14px)",
                },
              }}
              control={
                <Checkbox
                  checked={requestData?.cov_priv_pol_acc}
                  onChange={() =>
                    onChange({
                      ...requestData,
                      cov_priv_pol_acc: !requestData?.cov_priv_pol_acc,
                    })
                  }
                />
              }
              label={
                <>
                  I agree that Covestro Deutschland AG and its{" "}
                  <a
                    href="https://covestro.com/legal-entities-covestro"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    subsidiaries
                  </a>{" "}
                  can inform me via e-mail, telephone and via Social Media as
                  LinkedIn, LinkedIn Matched Audience, Facebook, Twitter,
                  YouTube, Instagram or WeChat of products of Covestro. An
                  overview of products of Covestro can be found{" "}
                  <a
                    href="https://solutions.covestro.com/en/products/?query=:relevanceName:countries:DE"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    here
                  </a>
                  . Further advertising information sent to me may encompass
                  product related trainings, product related webinars and other
                  product related events as well as information including white
                  papers, brochures, surveys and general news of Covestro. I
                  understand that I can withdraw my consent at any time with
                  effect for the future by either revoking my consent in
                  following this link{" "}
                  <a
                    href="https://www.covestro.com/en/legal/revocationform"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    here
                  </a>{" "}
                  or by clicking on the “unsubscribe link” integrated in each
                  communication which I receive in this context. Further
                  information about the data processing can be found in the{" "}
                  <a
                    href="https://solutions.covestro.com/en/footersites/privacy-statement"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    privacy statement
                  </a>
                  .
                </>
              }
            />
          </FormGroup>
        </Box>
      )}
    </DialogContent>
  );
};
